"use client";

import {
  normalizeSharedMediaProvider,
  type SharedMediaProvider,
} from "@/lib/adminSession";
import { useBoundStore } from "@/store";
import type { SessionMediaPlayRequest } from "@/store/slices/session.slice";

export type SessionMediaSearchDispatchMode = "spotify_api" | "store_action";
export type SessionMediaQueueMode = "none" | "search_results";
export type SessionMediaShuffleMode = "none" | "api" | "store_action";
export type SessionMediaTrackRemovalMode =
  | "none"
  | "spotify_uri_position"
  | "playlist_item_id";

export type SessionMediaTrackLike = {
  uri?: string | null;
  name?: string | null;
  artists?: Array<{ name?: string | null }> | null;
  album?: {
    name?: string | null;
    images?: Array<{ url?: string | null }> | null;
  } | null;
  duration_ms?: number | null;
  preview_url?: string | null;
  is_restricted?: boolean;
  restriction_reason?: string | null;
  restriction_reasons?: string[] | null;
};

export type SessionMediaProviderAdapter = {
  key: SharedMediaProvider;
  label: string;
  artworkFit: "contain" | "cover";
  supportsMediaSearch: boolean;
  supportsQueuePlayback: boolean;
  refreshOnWindowFocus: boolean;
  collaborativePlaylistsByDefault: boolean;
  search: {
    endpoint: (params: URLSearchParams) => string;
    playbackDispatchMode: SessionMediaSearchDispatchMode;
    queueMode: SessionMediaQueueMode;
  };
  playlists: {
    endpoint: string;
    tracksEndpoint: (playlistId: string) => string;
    mutationEndpoint: (playlistId: string) => string;
    mutationRefreshDelaysMs: readonly number[];
    addRequiresManagedPlaylist: boolean;
    removeRequiresManagedPlaylist: boolean;
    trackRemovalMode: SessionMediaTrackRemovalMode;
  };
  shuffle: {
    mode: SessionMediaShuffleMode;
    stateEndpoint: string | null;
    toggleEndpoint: string | null;
  };
};

type SessionMediaTrackPlayOptions = {
  queueUris?: string[];
  queueIndex?: number;
  queueSource?: string | null;
};

export type SessionMediaTrackRemovalRequest =
  | {
      endpoint: string;
      body: Record<string, unknown>;
      errorMessage: null;
    }
  | {
      endpoint: null;
      body: null;
      errorMessage: string;
    };

const DEFAULT_MUTATION_REFRESH_DELAYS_MS = [0] as const;
const YOUTUBE_MUTATION_REFRESH_DELAYS_MS = [0, 900, 2200, 5000, 9000] as const;

function buildPlaylistTracksEndpoint(basePath: string, playlistId: string) {
  return `${basePath}/playlists/${encodeURIComponent(playlistId)}/tracks`;
}

const SESSION_MEDIA_PROVIDER_ADAPTERS = {
  spotify: {
    key: "spotify",
    label: "Spotify",
    artworkFit: "cover",
    supportsMediaSearch: true,
    supportsQueuePlayback: false,
    refreshOnWindowFocus: false,
    collaborativePlaylistsByDefault: false,
    search: {
      endpoint: (params: URLSearchParams) => `/api/spotify/search?${params.toString()}`,
      playbackDispatchMode: "spotify_api",
      queueMode: "none",
    },
    playlists: {
      endpoint: "/api/spotify/playlists",
      tracksEndpoint: (playlistId: string) =>
        buildPlaylistTracksEndpoint("/api/spotify", playlistId),
      mutationEndpoint: (playlistId: string) =>
        buildPlaylistTracksEndpoint("/api/spotify", playlistId),
      mutationRefreshDelaysMs: DEFAULT_MUTATION_REFRESH_DELAYS_MS,
      addRequiresManagedPlaylist: true,
      removeRequiresManagedPlaylist: true,
      trackRemovalMode: "spotify_uri_position",
    },
    shuffle: {
      mode: "api",
      stateEndpoint: "/api/spotify/player/state",
      toggleEndpoint: "/api/spotify/player/shuffle",
    },
  },
  youtube: {
    key: "youtube",
    label: "YouTube",
    artworkFit: "contain",
    supportsMediaSearch: true,
    supportsQueuePlayback: false,
    refreshOnWindowFocus: true,
    collaborativePlaylistsByDefault: true,
    search: {
      endpoint: (params: URLSearchParams) => `/api/youtube/search?${params.toString()}`,
      playbackDispatchMode: "store_action",
      queueMode: "none",
    },
    playlists: {
      endpoint: "/api/youtube/playlists",
      tracksEndpoint: (playlistId: string) =>
        buildPlaylistTracksEndpoint("/api/youtube", playlistId),
      mutationEndpoint: (playlistId: string) =>
        buildPlaylistTracksEndpoint("/api/youtube", playlistId),
      mutationRefreshDelaysMs: YOUTUBE_MUTATION_REFRESH_DELAYS_MS,
      addRequiresManagedPlaylist: false,
      removeRequiresManagedPlaylist: false,
      trackRemovalMode: "playlist_item_id",
    },
    shuffle: {
      mode: "store_action",
      stateEndpoint: null,
      toggleEndpoint: null,
    },
  },
  applemusic: {
    key: "applemusic",
    label: "Apple Music",
    artworkFit: "cover",
    supportsMediaSearch: true,
    supportsQueuePlayback: true,
    refreshOnWindowFocus: false,
    collaborativePlaylistsByDefault: true,
    search: {
      endpoint: (params: URLSearchParams) => `/api/apple/search?${params.toString()}`,
      playbackDispatchMode: "store_action",
      queueMode: "search_results",
    },
    playlists: {
      endpoint: "/api/apple/playlists",
      tracksEndpoint: (playlistId: string) =>
        buildPlaylistTracksEndpoint("/api/apple", playlistId),
      mutationEndpoint: (playlistId: string) =>
        buildPlaylistTracksEndpoint("/api/apple", playlistId),
      mutationRefreshDelaysMs: DEFAULT_MUTATION_REFRESH_DELAYS_MS,
      addRequiresManagedPlaylist: false,
      removeRequiresManagedPlaylist: false,
      trackRemovalMode: "none",
    },
    shuffle: {
      mode: "store_action",
      stateEndpoint: null,
      toggleEndpoint: null,
    },
  },
} as const satisfies Record<SharedMediaProvider, SessionMediaProviderAdapter>;

function firstTrackImageUrl(track: SessionMediaTrackLike["album"]) {
  if (!track || !Array.isArray(track.images)) return null;
  for (const image of track.images) {
    if (typeof image?.url === "string" && image.url.trim().length > 0) {
      return image.url;
    }
  }
  return null;
}

function joinTrackArtists(artists: SessionMediaTrackLike["artists"]) {
  if (!Array.isArray(artists)) return "";
  return artists
    .map((artist) =>
      typeof artist?.name === "string" ? artist.name.trim() : "",
    )
    .filter(Boolean)
    .join(", ");
}

export function getSessionMediaProviderAdapter(
  provider: string | null | undefined,
) {
  const normalizedProvider = normalizeSharedMediaProvider(provider);
  return normalizedProvider
    ? SESSION_MEDIA_PROVIDER_ADAPTERS[normalizedProvider]
    : null;
}

export function dispatchSessionMediaTrackPlay(
  adapter: SessionMediaProviderAdapter | null | undefined,
  track: SessionMediaTrackLike,
  options: SessionMediaTrackPlayOptions = {},
) {
  if (!adapter || adapter.search.playbackDispatchMode !== "store_action") {
    return false;
  }
  if (adapter.key !== "youtube" && adapter.key !== "applemusic") {
    return false;
  }

  const detail: SessionMediaPlayRequest = {
    provider: adapter.key,
    uri: typeof track.uri === "string" ? track.uri : "",
    title:
      typeof track.name === "string" && track.name.trim().length > 0
        ? track.name
        : "Untitled",
    artist: joinTrackArtists(track.artists),
    imageUrl: firstTrackImageUrl(track.album),
    albumName:
      typeof track.album?.name === "string" && track.album.name.trim().length > 0
        ? track.album.name
        : null,
    durationMs:
      typeof track.duration_ms === "number" && Number.isFinite(track.duration_ms)
        ? Math.max(0, Math.trunc(track.duration_ms))
        : null,
    previewUrl:
      typeof track.preview_url === "string" && track.preview_url.trim().length > 0
        ? track.preview_url
        : null,
    isRestricted: typeof track.is_restricted === "boolean" ? track.is_restricted : null,
    restrictionReason:
      typeof track.restriction_reason === "string" &&
      track.restriction_reason.trim().length > 0
        ? track.restriction_reason
        : null,
    restrictionReasons:
      Array.isArray(track.restriction_reasons) && track.restriction_reasons.length > 0
        ? track.restriction_reasons.filter(
            (entry): entry is string =>
              typeof entry === "string" && entry.trim().length > 0
          )
        : null,
  };

  if (adapter.search.queueMode === "search_results") {
    detail.queueUris = options.queueUris ?? [];
    detail.queueIndex =
      typeof options.queueIndex === "number" ? options.queueIndex : undefined;
    detail.queueSource = options.queueSource ?? null;
  }

  useBoundStore.getState().requestMediaPlay(detail);
  return true;
}

export function dispatchSessionMediaShuffleToggle(
  adapter: SessionMediaProviderAdapter | null | undefined,
  enabled: boolean,
) {
  if (!adapter || adapter.shuffle.mode !== "store_action") {
    return false;
  }
  if (adapter.key !== "youtube" && adapter.key !== "applemusic") {
    return false;
  }

  useBoundStore.getState().requestMediaShuffle(adapter.key, enabled);
  return true;
}

export function scheduleSessionMediaPlaylistRefresh(
  adapter: SessionMediaProviderAdapter | null | undefined,
  bumpPlaylistRefresh: () => void,
) {
  if (typeof window === "undefined") {
    bumpPlaylistRefresh();
    return;
  }

  const refreshDelays =
    adapter?.playlists.mutationRefreshDelaysMs ??
    DEFAULT_MUTATION_REFRESH_DELAYS_MS;

  refreshDelays.forEach((delayMs) => {
    if (delayMs <= 0) {
      bumpPlaylistRefresh();
      return;
    }

    window.setTimeout(() => {
      bumpPlaylistRefresh();
    }, delayMs);
  });
}

export function buildSessionMediaTrackRemovalRequest(
  adapter: SessionMediaProviderAdapter | null | undefined,
  {
    playlistId,
    trackUri,
    trackIndex,
    playlistEntryId,
  }: {
    playlistId: string;
    trackUri: string;
    trackIndex: number;
    playlistEntryId: string | null;
  },
): SessionMediaTrackRemovalRequest {
  if (!adapter) {
    return {
      endpoint: null,
      body: null,
      errorMessage: "Removing tracks is not available for this playlist provider yet.",
    };
  }

  switch (adapter.playlists.trackRemovalMode) {
    case "spotify_uri_position":
      return {
        endpoint: adapter.playlists.mutationEndpoint(playlistId),
        body: {
          uri: trackUri,
          position: trackIndex,
        },
        errorMessage: null,
      };
    case "playlist_item_id":
      if (!playlistEntryId) {
        return {
          endpoint: null,
          body: null,
          errorMessage:
            "This playlist item is missing remove metadata. Refresh and try again.",
        };
      }
      return {
        endpoint: adapter.playlists.mutationEndpoint(playlistId),
        body: {
          playlistItemId: playlistEntryId,
        },
        errorMessage: null,
      };
    default:
      return {
        endpoint: null,
        body: null,
        errorMessage: "Removing tracks is not available for this playlist provider yet.",
      };
  }
}
